import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
    ComponentPropsWithoutRef,
    ReactNode,
    createContext,
    forwardRef,
    useContext,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./Accordion.module.scss";

export type AccordionVariant =
    | "default"
    | "list"
    | "listStatus"
    | "expandablePanel"
    | "nestedGroup";

export type AccordionSize = "large" | "small";

export type AccordionDisclosure = "chevron" | "caret";

type AccordionContextValue = {
    variant: AccordionVariant;
    size: AccordionSize;
};

const AccordionContext = createContext<AccordionContextValue>({
    variant: "default",
    size: "large",
});

const useAccordionContext = () => useContext(AccordionContext);

export type AccordionProps = ComponentPropsWithoutRef<
    typeof AccordionPrimitive.Root
> & {
    /** Visual variant per Figma Accordion matrix (`5294:63575`). */
    variant?: AccordionVariant;
    /** `large` = filters / panels; `small` = nav / flyouts. */
    size?: AccordionSize;
};

function AccordionRoot({
    className,
    variant = "default",
    size = "large",
    children,
    ...rest
}: AccordionProps) {
    const ctx: AccordionContextValue = { variant, size };

    return (
        <AccordionContext.Provider value={ctx}>
            <AccordionPrimitive.Root
                className={cx(styles.root, className)}
                data-size={size}
                data-ui="accordion"
                data-variant={variant}
                {...rest}
            >
                {children}
            </AccordionPrimitive.Root>
        </AccordionContext.Provider>
    );
}

const AccordionItem = forwardRef<
    HTMLDivElement,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(function AccordionItem({ className, ...props }, ref) {
    return (
        <AccordionPrimitive.Item
            className={cx(styles.item, className)}
            data-ui="accordion-item"
            ref={ref}
            {...props}
        />
    );
});

AccordionItem.displayName = "AccordionItem";

const AccordionHeader = forwardRef<
    HTMLHeadingElement,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Header>
>(function AccordionHeader({ className, ...props }, ref) {
    return (
        <AccordionPrimitive.Header
            className={cx(styles.header, className)}
            data-ui="accordion-header"
            ref={ref}
            {...props}
        />
    );
});

AccordionHeader.displayName = "AccordionHeader";

function ChevronGlyph() {
    return (
        <svg
            aria-hidden
            className={styles.chevronSvg}
            fill="none"
            height="100%"
            viewBox="0 0 20 20"
            width="100%"
        >
            <path
                d="M5 7.5l5 5 5-5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
            />
        </svg>
    );
}

export type AccordionTriggerProps = ComponentPropsWithoutRef<
    typeof AccordionPrimitive.Trigger
> & {
    /** Optional content before the label (e.g. extra icon). */
    leadingSlot?: ReactNode;
    /**
     * `chevron` = down/up (Default, List, Expandable panel).
     * `caret` = right/down (Nested group, Figma).
     */
    disclosure?: AccordionDisclosure;
    /** Omit the disclosure icon (rare; a11y must still convey expand/collapse). */
    hideIcon?: boolean;
};

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
    function AccordionTrigger(
        { className, children, leadingSlot, disclosure, hideIcon, ...props },
        ref,
    ) {
        const { variant } = useAccordionContext();
        const resolvedDisclosure: AccordionDisclosure =
            disclosure ?? (variant === "nestedGroup" ? "caret" : "chevron");
        const disclosureFirst =
            resolvedDisclosure === "caret" && variant === "nestedGroup";

        const icon = hideIcon ? null : (
            <span className={styles.disclosure}>
                <ChevronGlyph />
            </span>
        );

        return (
            <AccordionPrimitive.Trigger
                className={cx(styles.trigger, className)}
                data-disclosure={resolvedDisclosure}
                data-ui="accordion-trigger"
                ref={ref}
                {...props}
            >
                {disclosureFirst ? icon : null}
                {leadingSlot ? (
                    <span className={styles.leading}>{leadingSlot}</span>
                ) : null}
                <span className={styles.triggerLabel}>{children}</span>
                {!disclosureFirst ? icon : null}
            </AccordionPrimitive.Trigger>
        );
    },
);

AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = forwardRef<
    HTMLDivElement,
    ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(function AccordionContent({ className, children, ...props }, ref) {
    return (
        <AccordionPrimitive.Content
            className={cx(styles.content, className)}
            data-ui="accordion-content"
            ref={ref}
            {...props}
        >
            <div className={styles.contentInner}>{children}</div>
        </AccordionPrimitive.Content>
    );
});

AccordionContent.displayName = "AccordionContent";

/** Treez DS — Accordion (Figma `5294:63575`). Built on `@radix-ui/react-accordion`. */
export const Accordion = Object.assign(AccordionRoot, {
    Item: AccordionItem,
    Header: AccordionHeader,
    Trigger: AccordionTrigger,
    Content: AccordionContent,
});

AccordionRoot.displayName = "Accordion";
